import { useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { SignIn as ClerkSignIn } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useClerkAuth';
import { ArrowLeft } from 'lucide-react';
import { InAppBrowserWarning } from '@/components/InAppBrowserWarning';
import SEO from '@/hooks/useSEO';

export default function SignIn() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/');
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold">{t('common.loading')}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <SEO
        title="Sign In"
        description="Sign in to Lingua Craft and continue your German learning journey."
        canonicalPath="/sign-in"
        noindex
      />
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Welcome Back
          </h1>
          <p className="text-muted-foreground mt-2">
            Sign in to continue your German learning journey
          </p>
        </div>

        {/* OAuth Redirect Messages */}
        {searchParams.get('from') === 'oauth' && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="space-y-2">
              <p className="text-amber-800 text-sm font-medium">
                🔄 Redirected from {searchParams.get('provider') || 'Social'}{' '}
                Sign-in
              </p>
              <p className="text-amber-700 text-sm">
                There was an issue with social sign-in. Please try signing in
                manually below.
              </p>
            </div>
          </div>
        )}

        {/* Error Messages */}
        {searchParams.get('error') === 'oauth_failed' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">
              <strong>Authentication Failed:</strong> There was a problem with
              social sign-in. Please try again.
            </p>
          </div>
        )}

        {/* Sign In Form */}
        <Card className="bg-card/50 backdrop-blur-sm border-primary/20 p-6">
          {/* Show warning for in-app browsers */}
          <InAppBrowserWarning />

          <div className="flex justify-center">
            <ClerkSignIn
              fallbackRedirectUrl="/"
              signUpUrl="/sign-up"
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  card: 'shadow-none border-0 bg-transparent',
                },
              }}
            />
          </div>
        </Card>

        {/* Sign Up Link */}
        <div className="text-center mt-6">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link
              to="/sign-up"
              className="text-learning-primary hover:text-learning-primary/80 font-medium"
            >
              Sign up for free
            </Link>
          </p>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-4">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
